import { Box, Chip, Divider, Drawer, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import Moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import taskApi from '../../api/taskApi'
import ConfirmDialog from './ConfirmDialog'
import { useToast } from './ToastProvider'
import { errorMessage } from '../../utils/errors'
import '../../css/custom-editor.css'

const statusLabel = {
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Not saved'
}

const TaskPanel = ({ task, boardId, onClose, onUpdate, onDelete }) => {
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [status, setStatus] = useState('idle')
  const [confirmOpen, setConfirmOpen] = useState(false)

  const timer = useRef()
  const pending = useRef(null)
  const closed = useRef(true)
  const taskRef = useRef(undefined)

  const flush = useCallback(async () => {
    const current = taskRef.current
    const patch = pending.current
    if (!current || !patch) return

    pending.current = null
    try {
      await taskApi.update(boardId, current.id, patch)
      if (!closed.current) setStatus('saved')
    } catch (err) {
      // Surface the failure even when the panel already closed, otherwise the
      // last edit is dropped silently.
      if (!closed.current) setStatus('error')
      toast.error(errorMessage(err, 'Could not save the task.'))
    }
  }, [boardId, toast])

  useEffect(() => {
    if (!task) {
      closed.current = true
      return
    }
    if (pending.current) flush()

    closed.current = false
    taskRef.current = { ...task }
    setTitle(task.title || '')
    setContent(task.content || '')
    setStatus('idle')
    setConfirmOpen(false)
  }, [task, flush])

  useEffect(() => () => clearTimeout(timer.current), [])

  // Title and content edits are merged into one pending patch so a fast edit in
  // both fields still reaches the server as a single request.
  const persist = (patch) => {
    if (!taskRef.current) return
    pending.current = { ...(pending.current || {}), ...patch }
    clearTimeout(timer.current)
    setStatus('saving')
    timer.current = setTimeout(flush, 500)
  }

  const close = () => {
    clearTimeout(timer.current)
    flush()
    closed.current = true
    if (taskRef.current) onUpdate(taskRef.current)
    onClose()
  }

  const updateTitle = (event) => {
    const newTitle = event.target.value
    setTitle(newTitle)
    taskRef.current = { ...taskRef.current, title: newTitle }
    onUpdate(taskRef.current)
    persist({ title: newTitle })
  }

  const updateContent = (event, editor) => {
    if (closed.current) return
    const data = editor.getData()
    setContent(data)
    taskRef.current = { ...taskRef.current, content: data }
    onUpdate(taskRef.current)
    persist({ content: data })
  }

  const deleteTask = async () => {
    const current = taskRef.current
    if (!current) return
    clearTimeout(timer.current)
    pending.current = null
    try {
      await taskApi.delete(boardId, current.id)
      closed.current = true
      setConfirmOpen(false)
      onDelete(current)
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete the task.'))
    }
  }

  return (
    <>
      <Drawer
        anchor='right'
        open={Boolean(task)}
        onClose={close}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 760 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ px: { xs: 2, md: 3 }, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Typography variant='caption' color='text.disabled'>
              {task ? `Created ${Moment(task.createdAt).format('MMM D, YYYY')}` : ''}
            </Typography>
            {status !== 'idle' && (
              <Chip
                size='small'
                label={statusLabel[status]}
                color={status === 'error' ? 'warning' : 'default'}
                variant='outlined'
                sx={{ height: 20 }}
              />
            )}
          </Stack>

          <Stack direction='row' spacing={0.5}>
            <Tooltip title='Delete task'>
              <IconButton onClick={() => setConfirmOpen(true)} aria-label='Delete task'>
                <DeleteOutlineRoundedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Close'>
              <IconButton onClick={close} aria-label='Close task'>
                <CloseRoundedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 2.5, md: 5 },
            pt: 3,
            pb: 2
          }}
        >
          <TextField
            value={title}
            onChange={updateTitle}
            placeholder='Untitled task'
            variant='outlined'
            fullWidth
            aria-label='Task title'
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'transparent',
                paddingInline: 0,
                '& fieldset': { border: 'none' },
                fontFamily: "'Outfit', sans-serif",
                fontSize: { xs: '1.5rem', md: '1.875rem' },
                fontWeight: 600,
                letterSpacing: '-0.02em'
              },
              '& .MuiOutlinedInput-input': { padding: 0 }
            }}
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            <CKEditor
              editor={ClassicEditor}
              data={content}
              onChange={updateContent}
            />
          </Box>
        </Box>
      </Drawer>

      <ConfirmDialog
        open={confirmOpen}
        title='Delete this task?'
        description='The task and its notes will be removed. This cannot be undone.'
        confirmLabel='Delete task'
        onCancel={() => setConfirmOpen(false)}
        onConfirm={deleteTask}
      />
    </>
  )
}

export default TaskPanel
